import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCapabilities } from '../hooks/useCapabilities';
import { usePresenter } from '../components/presenter/usePresenter';
import { useWakeLock } from '../hooks/useWakeLock';
import { getMediaFileUrl } from '../api/media';
import { getAppleDeveloperToken } from '../api/music';
import { saveSessionFeedback } from '../api/sessions';
import { PresenterAudioManager } from '../components/presenter/PresenterAudioManager';
import './PresenterPage.css';

export default function PresenterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const audioManagerRef = useRef(null);
  if (!audioManagerRef.current) {
    audioManagerRef.current = new PresenterAudioManager();
  }
  const audioManager = audioManagerRef.current;
  const { user } = useAuth();
  const { isGuest } = useCapabilities();
  // Gäste kehren zur Umsonst-Liste zurück, eingeloggte Nutzer zur Referentenansicht
  const backTo = isGuest ? '/free' : '/presenter';
  const presenter = usePresenter(id, audioManager, { isGuest });
  useWakeLock(presenter.status === 'running' || presenter.status === 'paused');

  // Apple Music state + ref (ref avoids stale closures in async callbacks)
  const [musicKit, setMusicKit] = useState(null);
  const musicKitRef = useRef(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playlistName, setPlaylistName] = useState(null);
  const [musicWarning, setMusicWarning] = useState(false);
  const musicInitialized = useRef(false);

  // MusicKit JS-Script laden (falls noch nicht vorhanden)
  function ensureMusicKitScript() {
    if (window.MusicKit) return Promise.resolve(true);
    const existing = document.querySelector('script[src*="musickit"]');
    if (existing) {
      return new Promise((resolve) => {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => resolve(false));
      });
    }
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }

  // Apple Music initialisieren sobald Workout geladen (nur wenn playlist vorhanden)
  useEffect(() => {
    const playlistId = presenter.workout?.apple_music_playlist_id;
    const playlistEnabled = presenter.workout?.playlist_enabled;
    if (!playlistId || !playlistEnabled || !user?.music_integration_enabled || musicInitialized.current) return;

    const userToken = localStorage.getItem('apple_music_user_token');
    if (!userToken) {
      setMusicWarning(true);
      return;
    }

    musicInitialized.current = true;

    async function initMusicKit() {
      try {
        const loaded = await ensureMusicKitScript();
        if (!loaded || !window.MusicKit) return;
        const { developer_token } = await getAppleDeveloperToken();
        await window.MusicKit.configure({
          developerToken: developer_token,
          app: { name: 'MK Workout', build: '1.0' },
        });
        const kit = window.MusicKit.getInstance();
        kit.addEventListener('playbackStateDidChange', () => {
          setMusicPlaying(kit.playbackState === window.MusicKit.PlaybackStates.playing);
          setCurrentTrack(kit.nowPlayingItem?.attributes?.name || null);
        });
        musicKitRef.current = kit;
        setMusicKit(kit);
        audioManager.initMusic(kit);
        // Load queue to get playlist name — no play() here (iOS blocks autoplay without user gesture)
        const name = await audioManager.loadPlaylist(playlistId);
        setPlaylistName(name);
      } catch (e) {
        console.warn('Apple Music init fehlgeschlagen:', e);
      }
    }
    initMusicKit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenter.workout, user?.music_integration_enabled]);

  // Cleanup AudioManager on unmount
  useEffect(() => {
    return () => audioManager.destroy();
  }, [audioManager]);

  // Musik beim Phasenwechsel starten oder pausieren
  const lastPhaseIdRef = useRef(null);
  useEffect(() => {
    if (!musicKitRef.current || presenter.status !== 'running') return;
    const currentPhase = presenter.currentStep?.phase;
    if (!currentPhase) return;
    if (currentPhase.id === lastPhaseIdRef.current) return;
    lastPhaseIdRef.current = currentPhase.id;

    if (currentPhase.music_enabled !== false) {
      musicKitRef.current.play().catch(() => {});
    } else {
      musicKitRef.current.pause();
    }
  }, [presenter.currentStep?.phase?.id, presenter.status]);

  // Musik pausieren wenn Übung ein presenter_video hat, danach fortsetzen (Option A)
  const lastStepHadVideoRef = useRef(false);
  useEffect(() => {
    if (!musicKitRef.current || presenter.status !== 'running') return;
    const step = presenter.currentStep;
    const hasVideo = step?.type === 'exercise' &&
      (step.exercise?.media || []).some((m) => m.media_type === 'presenter_video');

    if (hasVideo && !lastStepHadVideoRef.current) {
      audioManager.pauseForVideo();
    } else if (!hasVideo && lastStepHadVideoRef.current) {
      audioManager.resumeAfterVideo();
    }
    lastStepHadVideoRef.current = !!hasVideo;
  }, [presenter.currentStep, presenter.status, audioManager]);

  if (presenter.error) {
    return (
      <FullscreenLayout centered>
        <div className="text-center text-danger">
          <i className="bi bi-exclamation-circle fs-1 d-block mb-3" />
          {presenter.error}
          <br />
          <button className="workout-control-btn-secondary mt-3" onClick={() => navigate(backTo)}>
            Zurück
          </button>
        </div>
      </FullscreenLayout>
    );
  }

  if (presenter.status === 'loading') {
    return (
      <FullscreenLayout centered>
        <div className="text-center text-white">
          <div className="spinner-border text-light mb-3" />
          <div>Lade Workout…</div>
        </div>
      </FullscreenLayout>
    );
  }

  if (presenter.status === 'ready') {
    return (
      <FullscreenLayout>
        <div className="flex-grow-1 d-flex align-items-center justify-content-center overflow-auto">
          <ReadyScreen
            presenter={presenter}
            onExit={() => navigate(backTo)}
            musicWarning={musicWarning}
            playlistName={playlistName}
            onStart={async () => {
              await audioManager.unlock();
              audioManager.startPlaylist(presenter.workout?.apple_music_playlist_id);
              presenter.start();
            }}
          />
        </div>
        {musicKit && (
          <MusicPanel
            playing={musicPlaying}
            trackName={currentTrack || playlistName}
            onToggle={() => {
              if (!musicPlaying) {
                audioManager.startPlaylist(presenter.workout?.apple_music_playlist_id);
              } else {
                audioManager.togglePlayback();
              }
            }}
          />
        )}
      </FullscreenLayout>
    );
  }

  if (presenter.status === 'completed') {
    return (
      <FullscreenLayout centered>
        <CompletedScreen
          workout={presenter.workout}
          sessionId={presenter.sessionId}
          onExit={() => { audioManager.stopMusic(); navigate(backTo); }}
        />
      </FullscreenLayout>
    );
  }

  // running or paused
  const { currentStep } = presenter;
  if (!currentStep) return null;

  const isPaused = presenter.status === 'paused';
  const isFirstStep = presenter.stepIndex === 0;
  const isLastStep = presenter.stepIndex >= presenter.steps.length - 1;

  const handleAbort = () => {
    presenter.pause();
    audioManager.stopMusic();
    navigate(backTo);
  };
  const handlePauseResume = () => {
    if (isPaused) {
      presenter.resume(presenter.timeLeft);
    } else {
      presenter.pause();
    }
  };

  return (
    <FullscreenLayout>
      {/* Top-Bar */}
      <div className="workout-control-topbar">
        <div className="workout-control-topbar__slot">
          <button
            className="workout-control-btn-icon-sm"
            onClick={handleAbort}
            aria-label="Workout beenden"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <div className="workout-control-topbar__slot workout-control-topbar__slot--center">
          <span className={`workout-control-phase-pill workout-control-phase-pill--${
            currentStep?.type === 'rest' ? 'rest' :
            currentStep?.type === 'phase_intro' ? 'ready' : 'work'
          }`}>
            {currentStep?.type === 'rest' ? 'PAUSE' :
             currentStep?.type === 'phase_intro' ? 'BEREIT' : 'ARBEIT'}
          </span>
        </div>
        <div className="workout-control-topbar__slot">
          {musicKit && currentStep?.phase?.music_enabled !== false && (
            <button
              className={`workout-control-btn-icon-sm ${musicPlaying ? 'is-active' : ''}`}
              onClick={() => audioManager.togglePlayback()}
              aria-label={musicPlaying ? 'Musik aus' : 'Musik an'}
            >
              <i className="bi bi-music-note-beamed" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex-shrink-0" style={{ height: 3, background: 'rgba(255,255,255,0.1)' }}>
        <div
          style={{
            height: '100%',
            width: `${presenter.steps.length > 0 ? ((presenter.stepIndex + 1) / presenter.steps.length) * 100 : 0}%`,
            background: 'var(--color-accent)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      <div className="flex-grow-1 d-flex align-items-center justify-content-center overflow-hidden">
        {currentStep.type === 'phase_intro' && (
          <PhaseIntroScreen step={currentStep} />
        )}
        {currentStep.type === 'exercise' && (
          <ExerciseScreen step={currentStep} presenter={presenter} />
        )}
        {currentStep.type === 'rest' && (
          <RestScreen step={currentStep} presenter={presenter} />
        )}
      </div>

      {/* Music Panel */}
      {musicKit && (
        <MusicPanel
          playing={musicPlaying}
          trackName={currentTrack || playlistName}
          onToggle={() => {
            if (!musicPlaying) {
              audioManager.startPlaylist(presenter.workout?.apple_music_playlist_id);
            } else {
              audioManager.togglePlayback();
            }
          }}
        />
      )}

      {/* Bottom-Bar: exercise / rest */}
      {currentStep && currentStep.type !== 'phase_intro' && (
        <div className="workout-control-bottombar">
          <button
            className="workout-control-btn-secondary"
            onClick={presenter.back}
            disabled={isFirstStep}
            aria-label="Vorheriger Schritt"
          >
            <i className="bi bi-skip-start-fill" />
          </button>
          <button
            className={`workout-control-btn-primary ${isPaused ? 'is-paused' : 'is-playing'}`}
            onClick={handlePauseResume}
            aria-label={isPaused ? 'Weiter' : 'Pause'}
          >
            <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--pause">
              <i className="bi bi-pause-fill" />
            </span>
            <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--play">
              <i className="bi bi-play-fill" />
            </span>
          </button>
          <button
            className="workout-control-btn-secondary"
            onClick={presenter.advance}
            disabled={isLastStep}
            aria-label="Nächster Schritt"
          >
            <i className="bi bi-skip-end-fill" />
          </button>
        </div>
      )}

      {/* Bottom-Bar: phase_intro */}
      {currentStep && currentStep.type === 'phase_intro' && (
        <div className="workout-control-bottombar">
          <div style={{ width: 'var(--workout-control-btn-secondary-size)' }} aria-hidden="true" />
          <button
            className="workout-control-btn-primary is-paused"
            onClick={presenter.advance}
            aria-label="Phase starten"
          >
            <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--pause">
              <i className="bi bi-pause-fill" />
            </span>
            <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--play">
              <i className="bi bi-play-fill" />
            </span>
          </button>
          <div style={{ width: 'var(--workout-control-btn-secondary-size)' }} aria-hidden="true" />
        </div>
      )}
    </FullscreenLayout>
  );
}

// ── Layout wrapper ────────────────────────────────────────────────────────────

function FullscreenLayout({ children, centered }) {
  return (
    <div
      // Presenter ist immer dunkel — unabhängig vom App-Hell/Dunkel-Modus.
      data-theme="dark"
      data-bs-theme="dark"
      className={`d-flex flex-column${centered ? ' align-items-center justify-content-center' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100dvh',
        background: 'var(--color-bg-presenter)',
        color: '#fff',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}


// ── Ready screen ──────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || '';

function getEmbedUrl(media) {
  const info = media?.meta_info;
  if (!info) return null;
  if (info.platform === 'youtube') return `https://www.youtube.com/embed/${info.video_id}`;
  if (info.platform === 'vimeo') return `https://player.vimeo.com/video/${info.video_id}`;
  return null;
}

function ReadyScreen({ presenter, onExit, musicWarning, playlistName, onStart }) {
  const { workout } = presenter;

  const exerciseCount = presenter.steps.filter((s) => s.type === 'exercise').length;
  const phaseCount = (workout?.phases || []).length;
  const introMedia = workout?.intro_media || [];
  const introImages = introMedia.filter((m) => m.media_type === 'image');
  const introVideo = introMedia.find((m) => m.media_type === 'video_url');
  const hasMedia = introImages.length > 0 || introVideo;

  return (
    <div
      className="text-center px-4 overflow-auto"
      style={{ maxWidth: hasMedia ? 860 : 520, maxHeight: '90vh', width: '100%' }}
    >
      <i className="bi bi-play-circle-fill mb-3" style={{ fontSize: '4rem', color: 'var(--color-accent)' }} />
      <h2 className="fw-bold mb-2">{workout?.name}</h2>
      {workout?.description && (
        <p className="text-secondary mb-3">{workout.description}</p>
      )}
      <div className="d-flex justify-content-center gap-4 mb-4 text-secondary small">
        <span><i className="bi bi-layers me-1" />{phaseCount} {phaseCount === 1 ? 'Phase' : 'Phasen'}</span>
        <span><i className="bi bi-activity me-1" />{exerciseCount} Übungen</span>
        {workout?.duration && (
          <span><i className="bi bi-clock me-1" />{workout.duration} Min.</span>
        )}
      </div>

      {/* Intro images */}
      {introImages.length > 0 && (
        <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
          {introImages.map((m) => (
            <img
              key={m.id}
              src={`${API_BASE}/api/media/${m.media_id}/file`}
              alt=""
              style={{ height: 180, maxWidth: '30%', objectFit: 'cover', borderRadius: 10 }}
            />
          ))}
        </div>
      )}

      {/* Intro video */}
      {introVideo && (
        <div className="mb-4 d-flex justify-content-center">
          {getEmbedUrl(introVideo.media) ? (
            <iframe
              src={getEmbedUrl(introVideo.media)}
              title="Intro-Video"
              style={{ width: '100%', maxWidth: 560, aspectRatio: '16/9', border: 'none', borderRadius: 10 }}
              allowFullScreen
            />
          ) : null}
        </div>
      )}

      {workout?.apple_music_playlist_id && workout?.playlist_enabled && !musicWarning && (
        <div className="d-flex align-items-center justify-content-center gap-2 mb-3 small" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <i className="bi bi-music-note-beamed" />
          <span>{playlistName || 'Apple Music'}</span>
        </div>
      )}

      {musicWarning && (
        <div className="alert alert-warning text-start mb-3 py-2">
          <i className="bi bi-music-note-beamed me-2" />
          Dieses Workout hat eine Apple Music Playlist, aber du bist nicht mit Apple Music
          verbunden. Gehe zu{' '}
          <a href="/settings" className="alert-link" target="_blank" rel="noreferrer">
            Einstellungen
          </a>{' '}
          und verbinde deinen Account, dann komm zurück.
        </div>
      )}

      <div className="d-flex flex-column gap-3" style={{ maxWidth: 480, margin: '0 auto' }}>
        <button
          className="workout-control-btn-fullwidth"
          onClick={onStart || presenter.start}
        >
          <i className="bi bi-play-fill" />
          Starten
        </button>
        <button
          className="workout-control-btn-fullwidth workout-control-btn-fullwidth--ghost"
          onClick={onExit}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}

// ── Music panel ───────────────────────────────────────────────────────────────

function MusicPanel({ playing, trackName, onToggle }) {
  return (
    <div
      className="d-flex align-items-center gap-3 px-4 flex-shrink-0"
      style={{
        background: 'rgba(0,0,0,0.6)',
        borderTop: '1px solid rgba(255,255,255,0.15)',
        minHeight: 60,
      }}
    >
      <i className="bi bi-music-note-beamed text-white opacity-75 fs-5" />
      <span className="text-white flex-grow-1 text-truncate opacity-75">
        {trackName || 'Apple Music'}
      </span>
      <button
        className={`workout-control-btn-icon-sm ${playing ? 'is-active' : ''}`}
        onClick={onToggle}
        aria-label={playing ? 'Musik pausieren' : 'Musik abspielen'}
      >
        <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`} />
      </button>
    </div>
  );
}

// ── Phase intro screen ────────────────────────────────────────────────────────

function PhaseIntroScreen({ step }) {
  return (
    <div className="text-center px-4">
      <h1
        className="fw-bold mb-5"
        style={{ fontSize: 'clamp(2.5rem, 8vw, 5rem)', lineHeight: 1.1 }}
      >
        {step.phase.name}
      </h1>
    </div>
  );
}

// ── Exercise screen ───────────────────────────────────────────────────────────

function ExerciseScreen({ step, presenter }) {
  const { item, exercise, round, set, roundRep, roundReps, setRep, setReps } = step;
  const hasDuration = Boolean(item.duration);
  const hasReps = Boolean(item.repetitions);

  const roundLabel = round?.name?.trim() || null;
  const setLabel = set?.name?.trim() || null;
  const showRoundCounter = roundReps > 1;
  const showSetCounter = setReps > 1;

  return (
    <div
      className="d-flex flex-column align-items-center text-center px-3 w-100"
      style={{ maxWidth: 860, maxHeight: '100%' }}
    >
      {/* Round / set names and counters */}
      {(roundLabel || setLabel || showRoundCounter || showSetCounter) && (
        <div className="d-flex flex-wrap justify-content-center gap-3 mb-2">
          {(roundLabel || showRoundCounter) && (
            <span
              className="px-3 py-1 rounded-pill"
              style={{ background: 'rgba(255,255,255,0.1)', fontSize: 'clamp(0.95rem, 2.5vw, 1.25rem)', fontWeight: 600 }}
            >
              <i className="bi bi-arrow-repeat me-2 opacity-75" />
              {roundLabel
                ? showRoundCounter
                  ? `${roundLabel} · ${roundRep}/${roundReps}`
                  : roundLabel
                : `Runde ${roundRep} / ${roundReps}`}
            </span>
          )}
          {(setLabel || showSetCounter) && (
            <span
              className="px-3 py-1 rounded-pill"
              style={{ background: 'rgba(255,255,255,0.1)', fontSize: 'clamp(0.95rem, 2.5vw, 1.25rem)', fontWeight: 600 }}
            >
              <i className="bi bi-layers me-2 opacity-75" />
              {setLabel
                ? showSetCounter
                  ? `${setLabel} · ${setRep}/${setReps}`
                  : setLabel
                : `Satz ${setRep} / ${setReps}`}
            </span>
          )}
        </div>
      )}

      {/* Media */}
      <div
        className="mb-3 w-100 rounded d-flex align-items-center justify-content-center"
        style={{ maxHeight: '38vh', background: 'rgba(255,255,255,0.05)' }}
      >
        <MediaDisplay exercise={exercise} />
      </div>

      {/* Exercise name */}
      <h2 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}>
        {exercise?.name}
      </h2>

      {/* Timer and/or reps */}
      <div className="my-2">
        {hasDuration ? (
          <div className="d-flex flex-column align-items-center gap-2">
            <TimerDisplay timeLeft={presenter.timeLeft} total={item.duration} />
            {hasReps && (
              <div className="text-secondary small">
                <i className="bi bi-arrow-repeat me-1" />
                mind. <strong className="text-white">{item.repetitions}</strong> Wiederholungen
              </div>
            )}
          </div>
        ) : hasReps ? (
          <RepDisplay reps={item.repetitions} />
        ) : null}
      </div>

      {/* Notes */}
      {item.notes && (
        <p className="text-secondary small mt-1 mb-0">{item.notes}</p>
      )}

      {/* Equipment */}
      {exercise?.equipment_needed && (
        <div className="text-secondary small mt-1">
          <i className="bi bi-tools me-1" />
          {exercise.equipment_needed}
        </div>
      )}
    </div>
  );
}

// ── Rest screen ───────────────────────────────────────────────────────────────

function RestScreen({ step, presenter }) {
  return (
    <div className="text-center px-3">
      <div
        className="fw-bold text-uppercase mb-4 px-5 py-3 rounded-4"
        style={{
          fontSize: 'clamp(3rem, 10vw, 6rem)',
          letterSpacing: '0.05em',
          background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
          color: '#fff',
          display: 'inline-block',
        }}
      >
        <i className="bi bi-hourglass-split me-3" />
        {step.label}
      </div>

      <div className="mt-2">
        <TimerDisplay timeLeft={presenter.timeLeft} total={step.duration} large />
      </div>

      {step.nextExercise && (
        <div className="mt-4 text-secondary">
          <div className="small mb-1">Als nächstes</div>
          <div className="fw-semibold fs-5">{step.nextExercise.name}</div>
          {step.nextExercise.equipment_needed && (
            <div className="small mt-1">
              <i className="bi bi-tools me-1" />
              {step.nextExercise.equipment_needed}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Completed screen ──────────────────────────────────────────────────────────

function CompletedScreen({ workout, sessionId, onExit }) {
  // Feedback nur, wenn eine Einheit angelegt wurde (eingeloggt, nicht Gast)
  const [rating, setRating] = useState(0);
  const [rpe, setRpe] = useState(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleDone() {
    if (sessionId && rating > 0) {
      setSaving(true);
      try {
        await saveSessionFeedback(sessionId, { rating, rpe, comment: comment.trim() || null });
      } catch {
        // Feedback ist optional – Abschluss nicht blockieren
      }
    }
    onExit();
  }

  return (
    <div className="text-center px-4" style={{ maxWidth: 520, margin: '0 auto' }}>
      <i className="bi bi-trophy-fill text-warning mb-3" style={{ fontSize: '4rem' }} />
      <h2 className="fw-bold mb-2">Workout abgeschlossen!</h2>
      <p className="text-secondary mb-4">{workout?.name}</p>

      {sessionId && (
        <div className="mb-4 text-start">
          <div className="text-center mb-3">
            <div className="small text-secondary mb-1">Wie war's?</div>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className="btn btn-link p-1 text-warning"
                style={{ fontSize: '2.2rem', lineHeight: 1 }}
                onClick={() => setRating(n)}
                aria-label={`${n} Sterne`}
              >
                <i className={`bi ${n <= rating ? 'bi-star-fill' : 'bi-star'}`} />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <>
              <div className="small text-secondary mb-1 text-center">
                Anstrengung (optional){rpe ? `: ${rpe}/10` : ''}
              </div>
              <div className="d-flex justify-content-center flex-wrap gap-1 mb-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`btn btn-sm ${rpe === n ? 'btn-warning' : 'btn-outline-secondary'}`}
                    style={{ minWidth: 36 }}
                    onClick={() => setRpe(rpe === n ? null : n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <textarea
                className="form-control"
                rows={2}
                maxLength={2000}
                placeholder="Kommentar (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </>
          )}
        </div>
      )}

      <button
        className="workout-control-btn-fullwidth"
        onClick={handleDone}
        disabled={saving}
        style={{ maxWidth: 480, margin: '0 auto' }}
      >
        <i className="bi bi-check-lg" />
        Fertig
      </button>
    </div>
  );
}

// ── Media display ─────────────────────────────────────────────────────────────

// iOS Safari blocks autoplay with mute=0. Detect iOS and mute for autoplay, desktop keeps audio on.
const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

function getLoopEmbedUrl(media) {
  const info = media?.meta_info;
  if (!info) return null;
  const mute = isIOS ? '1' : '0';
  if (info.platform === 'youtube')
    return `https://www.youtube.com/embed/${info.video_id}?autoplay=1&loop=1&playlist=${info.video_id}&mute=${mute}&controls=1&playsinline=1&enablejsapi=1`;
  if (info.platform === 'vimeo')
    return `https://player.vimeo.com/video/${info.video_id}?autoplay=1&loop=1&muted=${mute}&playsinline=1`;
  return null;
}

function VideoWithUnmute({ embedUrl, title }) {
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(isIOS);

  function handleUnmute() {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'unMute', args: [] }),
      '*'
    );
    setMuted(false);
  }

  return (
    <div style={{ width: 'min(100%, calc(38vh * 16 / 9))', maxWidth: 720 }}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={title}
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        style={{ width: '100%', aspectRatio: '16/9', border: 'none', borderRadius: 8, display: 'block' }}
      />
      {isIOS && muted && (
        <div className="text-center mt-2">
          <button
            className="workout-control-btn-fullwidth"
            onClick={handleUnmute}
            style={{ maxWidth: 280 }}
          >
            <i className="bi bi-volume-up" />
            Ton einschalten
          </button>
        </div>
      )}
    </div>
  );
}

function MediaDisplay({ exercise }) {
  if (!exercise) return <ExercisePlaceholder />;

  const mediaList = exercise.media || [];
  const presenterVideoRel = mediaList.find((m) => m.media_type === 'presenter_video');
  const presenterMedia = presenterVideoRel?.media;

  if (presenterMedia) {
    const embedUrl = getLoopEmbedUrl(presenterMedia);
    return embedUrl ? (
      <VideoWithUnmute key={presenterMedia.id} embedUrl={embedUrl} title={exercise.name} />
    ) : <ExercisePlaceholder />;
  }

  const images = mediaList.filter((m) => (m.media?.type || m.type) === 'image');
  if (images.length === 0) return <ExercisePlaceholder />;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 6,
        width: '100%',
        maxHeight: '34vh',
        padding: '0 8px',
        overflow: 'hidden',
      }}
    >
      {images.map((rel) => {
        const mid = rel.media?.id || rel.media_id;
        return (
          <div
            key={mid}
            style={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <img
              src={getMediaFileUrl(mid)}
              alt={exercise.name}
              style={{ maxWidth: '100%', maxHeight: '34vh', objectFit: 'contain', borderRadius: 6 }}
            />
          </div>
        );
      })}
    </div>
  );
}

function ExercisePlaceholder() {
  return (
    <div className="text-secondary py-4">
      <i className="bi bi-person-arms-up" style={{ fontSize: '5rem', opacity: 0.3 }} />
    </div>
  );
}

// ── Timer display ─────────────────────────────────────────────────────────────

function TimerDisplay({ timeLeft, total, large }) {
  const size = large ? 160 : 130;
  const stroke = large ? 8 : 7;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const progress = total > 0 ? (timeLeft ?? total) / total : 1;
  const dashOffset = circumference * (1 - progress);

  const mins = Math.floor((timeLeft ?? 0) / 60);
  const secs = (timeLeft ?? 0) % 60;
  const label = mins > 0
    ? `${mins}:${String(secs).padStart(2, '0')}`
    : `${timeLeft ?? 0}`;

  const color = progress > 0.33 ? 'var(--color-accent)' : '#dc3545';

  return (
    <div style={{ position: 'relative', width: size, height: size, display: 'inline-block' }}>
      <svg
        width={size}
        height={size}
        style={{ transform: 'rotate(-90deg)', display: 'block' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.05s linear, stroke 0.3s' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: large ? '2.5rem' : '1.9rem',
          fontWeight: 700,
          letterSpacing: -1,
        }}
      >
        {label}
        <span className="text-secondary ms-1" style={{ fontSize: '0.55em' }}>s</span>
      </div>
    </div>
  );
}

// ── Rep display ───────────────────────────────────────────────────────────────

function RepDisplay({ reps }) {
  return (
    <div className="text-center">
      <div
        style={{
          fontSize: 'clamp(3rem, 10vw, 6rem)',
          fontWeight: 800,
          lineHeight: 1,
          color: 'var(--color-accent)',
        }}
      >
        {reps}
      </div>
      <div className="text-secondary small mt-1">Wiederholungen</div>
    </div>
  );
}
